import React, { useState, useEffect, useMemo, useCallback, useImperativeHandle } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiRefreshCw, FiMoon, FiSun, FiSearch, FiList, FiCopy, FiArrowRight, FiChevronDown, FiLoader } from 'react-icons/fi';
import 'react-toastify/dist/ReactToastify.css';
import debounce from 'lodash/debounce';
import { isValidInput, isWithinValidRange } from '../../utils/unitConversions/inputValidation';

import { conversionData } from '../../utils/unitConversions/conversionData';
import ConversionExplanationDecimalToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationDecimalToAll';
import ConversionExplanationBinaryToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationBinaryToAll';
import ConversionExplanationOctalToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationOctalToAll';
import ConversionExplanationHexadecimalToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationHexadecimalToAll';
import ConversionExplanationBase36ToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationBase36ToAll';
import ConversionExplanationBase64ToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationBase64ToAll';
import ConversionExplanationRomanNumeralToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationRomanNumeralToAll';
import ConversionExplanationScientificNotationToAll from './ConversionExplanations/NumberConversionExplanation/ConversionExplanationScientificNotationToAll';
import DefaultConversionExplanation from './ConversionExplanations/DefaultConversionExplanation';
import { baseConversions, specialConversions, convertNumber } from '../../utils/unitConversions/number/numberUtils';
import { conversionCategories } from '../../utils/conversionCategories';
import TemperatureConversionExplanation from './ConversionExplanations/TemperatureConversionExplanation';
import LengthConversionExplanation from './ConversionExplanations/LengthConversionExplanation';
import { conversionExplanations } from './ConversionExplanations';
import { useDarkMode } from '../../context/DarkModeContext';

// Add this utility function at the top of the file
const formatDisplayValue = (value, unit) => {
  if (unit === 'Roman Numeral' || typeof value === 'string') {
    return value;
  }

  if (typeof value !== 'number') return '';
  
  // Handle special cases
  if (isNaN(value)) return 'Invalid Number';
  if (!isFinite(value)) return value > 0 ? '∞' : '-∞';
  
  // Convert to string and check length
  const stringValue = String(value);
  
  // If it's already in scientific notation, format it nicely
  if (stringValue.includes('e')) {
    const [base, exponent] = stringValue.split('e');
    const formattedBase = Number(base).toFixed(2);
    return `${formattedBase} × 10${superscriptNumber(exponent)}`;
  }
  
  // For very large or small numbers, convert to scientific notation
  if (Math.abs(value) >= 1e6 || (Math.abs(value) < 0.001 && value !== 0)) {
    const exp = Math.floor(Math.log10(Math.abs(value)));
    const base = value / Math.pow(10, exp);
    return `${base.toFixed(2)} × 10${superscriptNumber(exp)}`;
  }
  
  // For regular numbers, limit decimal places
  if (stringValue.includes('.')) {
    return value.toFixed(4);
  }
  
  return stringValue;
};

// Utility function to convert numbers to superscript
const superscriptNumber = (num) => {
  const superscripts = {
    '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
    '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹',
    '+': '⁺', '-': '⁻'
  };
  return String(num).split('').map(char => superscripts[char] || char).join('');
};

// Add this constant at the top of the file
const SUPPORTED_EXPLANATIONS = ['temperature', 'length'];

const UnitConversionCalculator = React.forwardRef(({ 
  initialSelectedConversion,
  initialFromUnit,
  initialToUnit,
  initialInputValue,
  previewMode = false,
  ...props 
}, ref) => {
  const { category, subConversion } = useParams();
  const { darkMode, toggleDarkMode } = useDarkMode();
  const [result, setResult] = useState(null);
  const [additionalParams, setAdditionalParams] = useState({});
  const [convertedValue, setConvertedValue] = useState(null);
  const [error, setError] = useState(null);
  const [explanationInput, setExplanationInput] = useState(null);
  const [showExplanation, setShowExplanation] = useState(true);
  const [showExample, setShowExample] = useState(true);
  const [copyButtonText, setCopyButtonText] = useState('Copy Result');
  const [inputError, setInputError] = useState(null);
  const [isEncodingMode, setIsEncodingMode] = useState(true);

  const [selectedConversion, setSelectedConversion] = useState(initialSelectedConversion || '');
  const [fromUnit, setFromUnit] = useState(initialFromUnit || '');
  const [toUnit, setToUnit] = useState(initialToUnit || '');
  const [inputValue, setInputValue] = useState(initialInputValue || '');

  const [isConverting, setIsConverting] = useState(false);

  // Add new error states
  const [conversionError, setConversionError] = useState(null);
  const [systemError, setSystemError] = useState(null);

  useEffect(() => {
    if (initialSelectedConversion) setSelectedConversion(initialSelectedConversion);
    if (initialFromUnit) setFromUnit(initialFromUnit);
    if (initialToUnit) setToUnit(initialToUnit);
    if (initialInputValue) setInputValue(initialInputValue);
  }, [initialSelectedConversion, initialFromUnit, initialToUnit, initialInputValue]);

  useEffect(() => {
    if (fromUnit === 'Decimal' && toUnit === 'Base-64') {
      setShowExample(true);
      setShowExplanation(true);
      setResult(null);
      setIsEncodingMode(true);
    }
  }, [fromUnit, toUnit]);

  useEffect(() => {
    // Set initial dark mode class
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []); // Run once on mount

  const availableUnits = useMemo(() => {
    return selectedConversion ? conversionData[selectedConversion].units : [];
  }, [selectedConversion]);

  const debouncedSetInputValue = useMemo(
    () => debounce((value) => setInputValue(value), 300),
    []
  );


  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputValue(value);
    setInputError(null);
    
    // Don't clear result immediately on input change
    // Only clear if the input becomes empty after trimming
    if (!value.trim()) {
      setResult(null);
      return;
    }

    // Special handling for Roman Numerals
    if (fromUnit === 'Roman Numeral') {
      // Validate format for Roman numerals, ignoring spaces
      if (!/^[MDCLXVI\s]*$/i.test(value)) {
        setInputError('Invalid Roman numeral');
        setResult(null); // Clear result only if input becomes invalid
      }
      return;
    }

    // Handle special values (trim spaces for comparison)
    const specialValues = {
      'infinity': Infinity,
      'inf': Infinity,
      '-infinity': -Infinity,
      '-inf': -Infinity,
      'nan': NaN
    };

    const lowerValue = value.trim().toLowerCase();
    if (specialValues.hasOwnProperty(lowerValue)) {
      setInputError('Special values are not supported');
      setResult(null);
      return;
    }

    // Regular validation for other number types
    if (!isValidInput(value.trim(), selectedConversion, fromUnit)) {
      setInputError(`Invalid input for ${fromUnit}`);
      setResult(null);
      return;
    }

    // Range check
    if (!isWithinValidRange(value.trim(), selectedConversion)) {
      setInputError(`Value out of range for ${fromUnit}`);
      setResult(null);
      return;
    }
  }, [selectedConversion, fromUnit]);

  const handleConversionChange = useCallback((e) => {
    const newConversion = e.target.value;
    setSelectedConversion(newConversion);
    
    // Set specific defaults for number conversion
    if (newConversion === 'number') {
      setFromUnit('Decimal');  // Set default fromUnit
      setToUnit('Base-64');    // Set default toUnit
      setShowExplanation(true);
      setShowExample(true);
      setIsEncodingMode(true);
    } else {
      // Reset for other conversion types
      setFromUnit('');
      setToUnit('');
      setShowExplanation(false);
      setShowExample(false);
      setIsEncodingMode(false);
    }
    
    // Common resets
    setInputValue('');
    setResult(null);
    setError(null);
    setExplanationInput(null);
  }, []);

  const getDefaultExampleValue = useCallback(() => {
    if (fromUnit === 'Decimal') return '42314.159';
    if (fromUnit === 'Binary') return '1010';
    if (fromUnit === 'Hexadecimal') return 'A5';
    if (fromUnit === 'Octal') return '52';
    if (fromUnit === 'Base-36') return 'Z9';
    if (fromUnit === 'Base-64') return 'SGVsbG8=';
    if (fromUnit === 'Roman Numeral') return 'XLII';
    if (fromUnit === 'Scientific Notation') return '4.2314e4';
    return '10'; // Default fallback
  }, [fromUnit]);

  const handleFromUnitChange = useCallback((e) => {
    const newFromUnit = e.target.value;
    
    // If selecting the same unit as toUnit, swap them
    if (newFromUnit === toUnit) {
      setFromUnit(newFromUnit);
      setToUnit(fromUnit);
    } else {
      setFromUnit(newFromUnit);
    }
    
    // Reset values
    setInputValue('');
    setResult(null);
    setExplanationInput(null);
    
    // Always show explanation for number conversion
    if (selectedConversion === 'number') {
      setShowExplanation(true);
      setShowExample(true);
    }
  }, [selectedConversion, fromUnit, toUnit]);

  const handleToUnitChange = useCallback((e) => {
    const newToUnit = e.target.value;
    
    // If selecting the same unit as fromUnit, swap them
    if (newToUnit === fromUnit) {
      setToUnit(newToUnit);
      setFromUnit(toUnit);
    } else {
      setToUnit(newToUnit);
    }
    
    setResult(null);
    setExplanationInput(null);
    
    if (selectedConversion === 'number') {
      setShowExplanation(true);
      setShowExample(true);
    }
  }, [selectedConversion, fromUnit, toUnit]);

  const handleAdditionalParamChange = useCallback((e) => {
    setAdditionalParams(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  }, []);

  const handleEncodingModeToggle = useCallback(() => {
    setIsEncodingMode(prevMode => !prevMode);
  }, []);

  const formatResult = useCallback((value, conversionType) => {
    if (typeof value !== 'number') return value;

    // Handle very large or small numbers
    const absValue = Math.abs(value);
    if (absValue > 1e9 || (absValue < 1e-9 && absValue > 0)) {
      return value.toExponential(6);
    }

    // Handle regular numbers with appropriate precision
    const precisionRules = {
      temperature: 2,
      length: 4,
      weight: 4,
      default: 6
    };

    const precision = precisionRules[conversionType] || precisionRules.default;
    return Number(value.toPrecision(precision)).toString();
  }, []);

  const checkPrecisionLoss = useCallback((input, result) => {
    // Check if conversion might result in precision loss
    const inputDigits = input.replace(/[.-]/g, '').length;
    const resultDigits = result.toString().replace(/[.-]/g, '').length;

    if (inputDigits > resultDigits + 2) { // Allow for some difference
      return true;
    }
    return false;
  }, []);

  // Add effect to auto-convert in preview mode when settings change
  useEffect(() => {
    if (previewMode && inputValue && fromUnit && toUnit) {
      handleConvert();
    }
  }, [previewMode, fromUnit, toUnit, additionalParams, isEncodingMode]); // Dependencies that should trigger auto-convert

  const handleConvert = useCallback(async () => {
    if (!inputValue || !fromUnit || !toUnit) return;

    setIsConverting(true);
    setError(null);
    setInputError(null);
    setConversionError(null);
    setSystemError(null);

    try {
      // Trim spaces from input value before conversion
      const trimmedValue = inputValue?.trim();

      if (!trimmedValue || !fromUnit || !toUnit) {
        setConversionError('Please enter a value to convert');
        setResult(null);
        setShowExplanation(false);
        return;
      }

      // Input validation
      const isValid = isValidInput(trimmedValue, selectedConversion, fromUnit);
      if (!isValid) {
        throw new Error(`Invalid input format for ${fromUnit}`);
      }

      let converted;
      try {
        if (selectedConversion === 'number') {
          converted = convertNumber(trimmedValue, fromUnit, toUnit, isEncodingMode);
        } else {
          const conversionFunction = conversionData[selectedConversion]?.convert;
          if (!conversionFunction) {
            throw new Error('Conversion type not supported');
          }
          converted = conversionFunction(trimmedValue, fromUnit, toUnit, additionalParams, isEncodingMode);
        }

        // Validate output
        if (converted === undefined || converted === null || Number.isNaN(converted)) {
          throw new Error('Invalid conversion result');
        }

      } catch (convError) {
        setConversionError(`Conversion failed: ${convError.message}`);
        return;
      }
      
      // Check for precision loss
      if (checkPrecisionLoss(trimmedValue, converted)) {
        setResult({
          input: trimmedValue,
          converted,
          fromUnit,
          toUnit,
          warning: 'Some precision may have been lost in this conversion'
        });
      } else {
        setResult({
          input: trimmedValue,
          converted,
          fromUnit,
          toUnit
        });
      }
      setConvertedValue(converted);
      setExplanationInput(trimmedValue);
      setShowExplanation(true);
      setShowExample(false);

      // Notify parent of successful conversion
      if (props.onConversionComplete) {
        props.onConversionComplete({
          category: selectedConversion,
          fromUnit,
          toUnit,
          inputValue: trimmedValue,
          result: converted,
        });
      }

    } catch (error) {
      // Handle system-level errors
      console.error('System error:', error);
      setSystemError('An unexpected error occurred. Please try again.');
      
      // Throw to ErrorBoundary if it's a critical error
      if (error instanceof TypeError || error instanceof ReferenceError) {
        throw error;
      }
    } finally {
      setIsConverting(false);
    }
  }, [selectedConversion, inputValue, fromUnit, toUnit, additionalParams, isEncodingMode]);

  const copyToClipboard = useCallback(() => {
    if (result) {
      // Use the raw value for copying, not the formatted display value
      const textToCopy = String(result.converted);
      navigator.clipboard.writeText(textToCopy).then(() => {
        setCopyButtonText('Copied!');
        setTimeout(() => setCopyButtonText('Copy Result'), 2000);
      }).catch(err => {
        console.error('Failed to copy: ', err);
        setCopyButtonText('Failed to copy');
        setTimeout(() => setCopyButtonText('Copy Result'), 2000);
      });
    }
  }, [result]);

  const renderExplanation = useCallback(() => {
    // Early return if not showing explanation
    if (!showExplanation) {
      return null;
    }

    // Define explanation props
    const explanationProps = {
      conversionType: selectedConversion,
      fromUnit,
      toUnit,
      inputValue: explanationInput,
      result: result ? result.converted : null,
      darkMode,
      isEncodingMode,
      onEncodingModeToggle: handleEncodingModeToggle,
      showExample: !result || !explanationInput, // Show example when there's no result or no input
      showExplanation,
    };

    // Handle number conversions separately since they have their own explanation system
    if (selectedConversion === 'number') {
      let ExplanationComponent;
      switch (fromUnit) {
        case 'Decimal':
          ExplanationComponent = ConversionExplanationDecimalToAll;
          break;
        case 'Binary':
          ExplanationComponent = ConversionExplanationBinaryToAll;
          break;
        case 'Octal':
          ExplanationComponent = ConversionExplanationOctalToAll;
          break;
        case 'Hexadecimal':
          ExplanationComponent = ConversionExplanationHexadecimalToAll;
          break;
        case 'Base-36':
          ExplanationComponent = ConversionExplanationBase36ToAll;
          break;
        case 'Base-64':
          ExplanationComponent = ConversionExplanationBase64ToAll;
          break;
        case 'Roman Numeral':
          ExplanationComponent = ConversionExplanationRomanNumeralToAll;
          break;
        case 'Scientific Notation':
          ExplanationComponent = ConversionExplanationScientificNotationToAll;
          break;
        default:
          ExplanationComponent = DefaultConversionExplanation;
      }
      return <ExplanationComponent {...explanationProps} />;
    }

    // For non-number conversions, check if explanation is supported
    if (!SUPPORTED_EXPLANATIONS.includes(selectedConversion)) {
      return (
        <div className="mt-8 p-6 rounded-xl bg-gray-50 dark:bg-gray-900/50 
                      border border-gray-200/80 dark:border-gray-700/50">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-50 dark:bg-blue-900/30 
                           flex items-center justify-center">
              <svg className="w-6 h-6 text-blue-500 dark:text-blue-400" 
                   fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                Explanation Coming Soon
              </h3>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                We're currently working on detailed explanations for this conversion type. 
                Our team is crafting comprehensive, step-by-step guides to help you 
                understand the conversion process better.
              </p>
              <div className="mt-4 flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span>Check back soon for updates</span>
              </div>
            </div>
          </div>
        </div>
      );
    }

    // Handle supported non-number conversions
    let ExplanationComponent;
    if (conversionExplanations[selectedConversion]) {
      ExplanationComponent = conversionExplanations[selectedConversion];
    } else {
      ExplanationComponent = DefaultConversionExplanation;
    }

    return <ExplanationComponent {...explanationProps} />;
  }, [
    selectedConversion, 
    fromUnit, 
    toUnit, 
    showExplanation, 
    result, 
    explanationInput, 
    darkMode, 
    isEncodingMode,
    handleEncodingModeToggle
  ]);


  const generateSubConversions = useCallback((units) => {
    const subConversions = [];
    for (let i = 0; i < units.length; i++) {
      for (let j = 0; j < units.length; j++) {
        if (i !== j) {
          subConversions.push(`${units[i]} to ${units[j]}`);
        }
      }
    }
    return subConversions;
  }, []);

  useEffect(() => {
    if (fromUnit && toUnit) {
      setShowExample(true);
      setShowExplanation(true);
      setResult(null);
      if (selectedConversion === 'number') {
        setIsEncodingMode(true);
      }
    }
  }, [fromUnit, toUnit, selectedConversion]);

  // Add an effect to handle initial number system state
  useEffect(() => {
    if (selectedConversion === 'number') {
      // If both units are the same or not set properly
      if (!fromUnit || !toUnit || fromUnit === toUnit) {
        setFromUnit('Decimal');
        setToUnit('Base-64');
        setShowExplanation(true);
        setShowExample(true);
        setIsEncodingMode(true);
      }
    }
  }, [selectedConversion, fromUnit, toUnit]);

  // Update the title and badges section
  const renderHeader = () => {
    const category = conversionCategories[selectedConversion] || {
      title: 'Unit Converter',
      primaryBadge: { text: 'Standard Units', color: 'gray' },
      secondaryBadge: 'Unit Systems'
    };

    return (
      <div className="border-b border-gray-200/80 dark:border-gray-700/80 pb-6 mb-8">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <h2 className="text-[22px] font-semibold bg-gradient-to-r 
                           from-primary-600 via-primary-500 to-primary-400 
                           bg-clip-text text-transparent tracking-tight">
              {category.title}
            </h2>
            
            <div className="flex items-center gap-3">
              {/* Primary Badge */}
              <span className={`inline-flex items-center text-[13px] font-medium
                ${darkMode ? 'text-primary-400 bg-primary-900/30 px-2 py-0.5 rounded-full' : `text-${category.primaryBadge.color}-600`}
              `}>
                <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {category.primaryBadge.text}
              </span>

              <span className="h-4 w-[1px] bg-gray-300 dark:bg-gray-700"></span>

              {/* Secondary Badge */}
              <span className="text-[13px] text-gray-600 dark:text-neutral-400">
                {category.secondaryBadge}
              </span>

              {/* Tertiary Badge (if exists) */}
              {category.tertiaryBadge && (
                <>
                  <span className="h-4 w-[1px] bg-gray-300 dark:bg-gray-700"></span>
                  <span className="text-[13px] text-gray-600 dark:text-neutral-400">
                    {category.tertiaryBadge}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Dark mode toggle button */}
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={toggleDarkMode}
            className="p-2.5 rounded-lg bg-gray-50 dark:bg-gray-800/90 
                     hover:bg-gray-100 dark:hover:bg-gray-700/80 
                     transition-all duration-200 shadow-sm"
            aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {darkMode ? <FiSun className="w-5 h-5" /> : <FiMoon className="w-5 h-5" />}
          </motion.button>
        </div>
      </div>
    );
  };

  // Expose convert method through ref
  useImperativeHandle(ref, () => ({
    convert: handleConvert
  }), [handleConvert]);

  const handleConversionTypeChange = (event) => {
    const newConversionType = event.target.value;
    setSelectedConversion(newConversionType);
    
    // Set meaningful default conversion pairs for each category
    switch(newConversionType) {
      case 'number':
        setFromUnit('Decimal');
        setToUnit('Binary');
        break;
      case 'weight':
        setFromUnit('Kilograms');
        setToUnit('Pounds');
        break;
      case 'length':
        setFromUnit('Meters');
        setToUnit('Feet');
        break;
      case 'power':
        setFromUnit('Watts');
        setToUnit('Horsepower');
        break;
      case 'voltage':
        setFromUnit('Volts');
        setToUnit('Kilovolts');
        break;
      case 'energy':
        setFromUnit('Joules');
        setToUnit('Kilowatt-hours');
        break;
      case 'electricCharge':
        setFromUnit('Coulombs');
        setToUnit('Ampere-hours');
        break;
      case 'frequency':
        setFromUnit('Hertz');
        setToUnit('Kilohertz');
        break;
      case 'temperature':
        setFromUnit('Celsius');
        setToUnit('Fahrenheit');
        break;
      case 'color':
        setFromUnit('RGB');
        setToUnit('HEX');
        break;
      case 'resistance':
        setFromUnit('Ohms');
        setToUnit('Kilohms');
        break;
      case 'image':
        setFromUnit('JPEG');
        setToUnit('PNG');
        break;
      default:
        // Fallback to first two units if category is not handled
        const availableUnits = conversionData[newConversionType]?.units || [];
        if (availableUnits.length >= 2) {
          setFromUnit(availableUnits[0]);
          setToUnit(availableUnits[1]);
        }
    }

    // Clear the input and result when changing conversion type
    setInputValue('');
    setResult(null);
    setConversionError(null);
  };

  // If in preview mode, simplify the UI
  if (previewMode) {
    return (
      <div className="max-w-full">
        <div className="space-y-6">
          {/* Input section with unit label above */}
          <div className="flex gap-3">
            <div className="flex-1">
              <div className="mb-1 text-sm text-gray-500">
                {fromUnit}
              </div>
              <input
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="w-full px-4 py-2 rounded-lg border border-gray-200 
                         focus:ring-2 focus:ring-emerald-500 focus:border-transparent
                         text-sm
                         bg-white dark:bg-gray-900
                         text-gray-900 dark:text-neutral-100
                         placeholder-gray-400 dark:placeholder-neutral-400
                         border-gray-200 dark:border-gray-700
                         hover:border-gray-300 dark:hover:border-gray-600
                         transition-colors duration-200"
                placeholder={`Enter a ${fromUnit.toLowerCase()} number...`}
              />
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setShowExample(false); // Hide example when converting
                handleConvert();
              }}
              className="self-end px-4 py-2 bg-emerald-500 text-white rounded-lg 
                       font-medium text-sm shadow-sm hover:bg-emerald-600 
                       transition-colors duration-200 flex items-center gap-2 whitespace-nowrap"
            >
              <FiRefreshCw className={`w-4 h-4 ${isConverting ? 'animate-spin' : ''}`} />
              Convert
            </motion.button>
          </div>

          {/* Result with unit label above */}
          {result && !showExample && (
            <div>
              <div className="mb-1 text-sm text-gray-500">
                {toUnit}
              </div>
              <div className="px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                <span className="text-emerald-600 font-medium text-lg">
                  {formatDisplayValue(result.converted, toUnit)}
                </span>
              </div>
            </div>
          )}

          {/* Explanation section */}
          {showExplanation && renderExplanation()}
        </div>
      </div>
    );
  }

  // Regular component render
  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-3xl">
        <div className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl 
                        border border-gray-200 dark:border-gray-700/80
                        p-4 sm:p-6 lg:p-8 transition-all duration-300 ease-in-out
                        ${darkMode ? 'bg-gradient-to-br from-gray-900/90 to-gray-950/80 shadow-2xl' : ''}`}
        >
          {renderHeader()}

          <div className="mb-8">
            <label htmlFor="conversion-type" 
                   className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Conversion Type
            </label>
            <div className="relative">
              <select
                id="conversion-type"
                value={selectedConversion}
                onChange={handleConversionTypeChange}
                aria-label="Select conversion type"
                className="w-full appearance-none bg-gray-50 dark:bg-gray-800/90 
                           border border-gray-200 dark:border-gray-700
                           rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-primary-500/30 
                           focus:border-blue-500/60 dark:focus:border-primary-500/40
                           focus:shadow-[0_0_0_3px_rgba(16,185,129,0.10)] dark:focus:shadow-[0_0_0_3px_rgba(16,185,129,0.20)]
                           transition-all duration-200
                           hover:border-gray-300 dark:hover:border-gray-600"
              >
                <option value="number">Number Systems</option>
                <option value="weight">Weight Conversion</option>
                <option value="length">Length Conversion</option>
                <option value="power">Power Conversion</option>
                <option value="voltage">Voltage Conversion</option>
                <option value="energy">Energy Conversion</option>
                <option value="electric-charge">Electric Charge Conversion</option>
                <option value="frequency">Frequency Conversion</option>
                <option value="temperature">Temperature Conversion</option>
                <option value="color">Color Conversion</option>
                <option value="resistance">Resistance Conversion</option>
                <option value="image">Image Conversion</option>
              </select>
              <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none
                                text-gray-400 dark:text-gray-500">
                <FiChevronDown className="w-5 h-5" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 mb-8">
            <div className="space-y-1.5">
              <label htmlFor="from-unit" 
                     className="text-sm font-medium text-gray-700 dark:text-gray-300">
                From
              </label>
              <div className="relative">
                <select
                  id="from-unit"
                  value={fromUnit}
                  onChange={handleFromUnitChange}
                  className="w-full appearance-none bg-gray-50 dark:bg-gray-800/90 
                             border border-gray-200 dark:border-gray-700
                             rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100
                             focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-primary-500/30 
                             focus:border-blue-500/60 dark:focus:border-primary-500/40
                             focus:shadow-[0_0_0_3px_rgba(16,185,129,0.10)] dark:focus:shadow-[0_0_0_3px_rgba(16,185,129,0.20)]
                             transition-all duration-200
                             hover:border-gray-300 dark:hover:border-gray-600"
                >
                  {availableUnits.map((unit) => (
                    <option key={unit} value={unit}>
                      {unit}
                    </option>
              ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none
                                  text-gray-400 dark:text-gray-500">
                  <FiChevronDown className="w-5 h-5" />
                </div>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <label htmlFor="to-unit" 
                     className="text-sm font-medium text-gray-700 dark:text-gray-300">
                To
              </label>
              <div className="relative">
                <select
                  id="to-unit"
                  value={toUnit}
                  onChange={handleToUnitChange}
                  className="w-full appearance-none bg-gray-50 dark:bg-gray-800/90 
                             border border-gray-200 dark:border-gray-700
                             rounded-lg px-4 py-2.5 text-gray-900 dark:text-gray-100
                             focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-primary-500/30 
                             focus:border-blue-500/60 dark:focus:border-primary-500/40
                             focus:shadow-[0_0_0_3px_rgba(16,185,129,0.10)] dark:focus:shadow-[0_0_0_3px_rgba(16,185,129,0.20)]
                             transition-all duration-200
                             hover:border-gray-300 dark:hover:border-gray-600"
                >
                  {availableUnits
                    .filter((unit) => unit !== fromUnit)
                    .map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
              ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none
                                  text-gray-400 dark:text-gray-500">
                  <FiChevronDown className="w-5 h-5" />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-8">
            <label htmlFor="value-input" 
                   className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5 block">
              Value
            </label>
            <div className="relative">
              <input
                id="value-input"
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                aria-label="Enter value to convert"
                aria-invalid={!!inputError}
                aria-describedby={inputError ? "input-error" : undefined}
                className={`w-full bg-gray-50 dark:bg-gray-800/90 
                    border ${inputError ? 'border-red-500 dark:border-red-500/50' : 
                                        'border-gray-200 dark:border-gray-700'}
                    rounded-lg px-4 py-2.5 
                    text-gray-900 dark:text-gray-100
                    focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-primary-500/30 
                    focus:border-blue-500/60 dark:focus:border-primary-500/40
                    focus:shadow-[0_0_0_3px_rgba(16,185,129,0.10)] dark:focus:shadow-[0_0_0_3px_rgba(16,185,129,0.20)]
                    transition-all duration-200
                    placeholder-gray-400 dark:placeholder-neutral-400
                    hover:border-gray-300 dark:hover:border-gray-600`}
                placeholder={`Enter ${fromUnit} value`}
              />
              {inputError && (
                <p id="input-error" role="alert" className="absolute -bottom-5 left-0 text-xs text-red-500 mt-1">
                  {inputError}
                </p>
              )}
            </div>
          </div>

          {/* Additional params for image conversion */}
          {selectedConversion === 'image' && (
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <label className="block mb-2">Width (pixels)</label>
                <input
                  type="number"
                  name="width"
                  value={additionalParams.width || ''}
                  onChange={handleAdditionalParamChange}
                  className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} focus:shadow-[0_0_0_2px_rgba(16,185,129,0.15)] dark:focus:shadow-[0_0_0_2px_rgba(16,185,129,0.25)] transition-all duration-200`}
                />
              </div>
              <div>
                <label className="block mb-2">Height (pixels)</label>
                <input
                  type="number"
                  name="height"
                  value={additionalParams.height || ''}
                  onChange={handleAdditionalParamChange}
                  className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} focus:shadow-[0_0_0_2px_rgba(16,185,129,0.15)] dark:focus:shadow-[0_0_0_2px_rgba(16,185,129,0.25)] transition-all duration-200`}
                />
              </div>
              <div>
                <label className="block mb-2">PPI/DPI</label>
                <input
                  type="number"
                  name="ppi"
                  value={additionalParams.ppi || ''}
                  onChange={handleAdditionalParamChange}
                  className={`w-full p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'} focus:shadow-[0_0_0_2px_rgba(16,185,129,0.15)] dark:focus:shadow-[0_0_0_2px_rgba(16,185,129,0.25)] transition-all duration-200`}
                />
              </div>
            </div>
          )}

          <motion.button
            whileHover={{ scale: 1.02, boxShadow: darkMode ? '0 0 0 6px rgba(16,185,129,0.10)' : '0 0 0 4px rgba(139,92,246,0.10)' }}
            whileTap={{ scale: 0.98, opacity: 0.95 }}
            onClick={handleConvert}
            disabled={!fromUnit || !toUnit || !inputValue || isConverting}
            className={`w-full p-3 text-white rounded-lg transition-all duration-300 
              ${!fromUnit || !toUnit || !inputValue || isConverting
                ? 'bg-gray-400 dark:bg-gray-600 cursor-not-allowed'
                : 'bg-purple-600 hover:bg-purple-700 dark:bg-primary-700 dark:hover:bg-primary-600 shadow-lg shadow-primary-500/20 dark:shadow-primary-500/30'}
            `}
          >
            {isConverting ? (
              <span className="flex items-center justify-center">
                <FiLoader className="animate-spin mr-2" />
                Converting...
              </span>
            ) : (
              <>
                <FiRefreshCw className="inline-block mr-2" />
                Convert
              </>
            )}
          </motion.button>

          {/* Error Displays */}
          {(conversionError || systemError || inputError) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 p-4 rounded-lg border"
              style={{
                backgroundColor: systemError ? 'rgb(254 226 226)' : 'rgb(254 242 242)',
                borderColor: systemError ? 'rgb(248 113 113)' : 'rgb(252 165 165)',
              }}
            >
              <div className="flex items-center">
                <svg
                  className="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  {systemError && (
                    <p className="text-red-800 font-medium">System Error</p>
                  )}
                  <p className="text-red-600">
                    {systemError || conversionError || inputError}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {result && result.warning && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-2 p-3 bg-yellow-50 dark:bg-yellow-900/20 
                         rounded-md border border-yellow-200 dark:border-yellow-700/50"
            >
              <div className="flex items-center text-yellow-800 dark:text-yellow-200">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                {result.warning}
              </div>
            </motion.div>
          )}

          {result && !showExample && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="mt-6 mb-6 p-6 bg-gradient-to-r from-green-50/90 to-blue-50/90 
                         dark:from-blue-900/60 dark:to-gray-900/80 dark:bg-gray-900/80
                         rounded-lg shadow-lg relative
                         border border-gray-200/50 dark:border-blue-800"
            >
              <h3 className="text-2xl font-bold mb-4 text-gray-800 dark:text-gray-200">
                Conversion Result
              </h3>
              <div className="flex flex-col space-y-3">
                {/* Input Value */}
                <div className="flex flex-col sm:flex-row items-center justify-center 
                              text-lg sm:text-xl font-medium space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-2 overflow-x-auto max-w-full 
                                scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                    <span className="text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      {formatDisplayValue(result.input, fromUnit)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {result.fromUnit}
                    </span>
                  </div>
                </div>

                {/* Equals Sign */}
                <div className="flex justify-center">
                  <span className="text-gray-500 dark:text-gray-400">=</span>
                </div>

                {/* Result Value */}
                <div className="flex flex-col sm:flex-row items-center justify-center 
                              text-lg sm:text-xl font-medium space-y-2 sm:space-y-0 sm:space-x-4">
                  <div className="flex items-center space-x-2 overflow-x-auto max-w-full 
                                scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-600">
                    <span className="text-blue-600 dark:text-emerald-400 whitespace-nowrap font-bold text-2xl">
                      {formatDisplayValue(result.converted, toUnit)}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {result.toUnit}
                    </span>
                  </div>
                </div>
              </div>

              {/* Copy button */}
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={copyToClipboard}
                className="absolute top-2 right-2 p-1 text-gray-400 hover:text-gray-600 
                  dark:text-gray-500 dark:hover:text-gray-300 
                  transition-colors duration-300 focus:outline-none 
                  focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 rounded-full"
                title={copyButtonText}
              >
                <FiCopy className="w-4 h-4" />
              </motion.button>
            </motion.div>
          )}

          {/* Explanation Area */}
          <div className="mt-8">
            <div className="rounded-2xl transition-all duration-300
              dark:bg-gray-900/80 dark:border dark:border-gray-800 dark:shadow-2xl">
          {renderExplanation()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});


export default React.memo(UnitConversionCalculator);